import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Dropdown, notification } from 'antd';
import { EllipsisOutlined } from 'antd/icons';
import useUser from 'vl-core/hooks/useUser';
import * as actions from '@actions';
import usePagedQuery from 'vl-common/src/hooks/usePagedQuery';
import useTransformColumns from 'vl-common/src/hooks/useTransformColumns';
import { useDispatch } from 'wrapped-redux';
import Pagination from '../Table/Pagination';
import useColumnFilters from 'vl-common/src/hooks/useColumnFilters';
import useSystemType from 'vl-core/hooks/useSystemType';
import moment from 'moment/moment';
import qs from 'vl-common/src/lib/query-string';
import useReactiveColumns from 'vl-common/src/hooks/useReactiveColumns';
import { DataCell, PinnedPosition, TableDataView } from 'vl-common/src/components/Table/TableDataView';
import useWidthBreakpoint from 'vl-common/src/hooks/useWidthBreakpoint';
import useRouter from 'vl-core/useRouter';
import ConfigLink from 'vl-core/config.link';
import { timelineBackQueryParam } from '../TimeLine/timelineBackTarget';
import useUserType, { UserType } from 'vl-common/src/hooks/useUserType';
import useFetchClientList from '../../hooks/useFetchClientList';
import usePatientManagementActions from '../../hooks/usePatientManagementActions';
import ManagePatientContextProvider, { PatientType } from 'vl-common/src/lib/context/ManagePatientContext';
import AddPatient from '../AddPatient/AddPatient';
import { successModal } from '../Modals';
import useApi from 'vl-common/src/hooks/useApi';
import useTableColumnSettings from '../Table/useTableColumnSettings';
import useTableAutoZoom from 'vl-common/src/hooks/useTableAutoZoom';

const PAGE_SIZE = 10;
const PATIENTS_TABLE_ID = 'patients';

function PatientSearchTable({ adminProps }: any) {
  const { t } = useTranslation('common');
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const systemType = useSystemType();
  const dispatch = useDispatch();
  const user = useUser();
  const router = useRouter();
  const userType = useUserType();
  const clientList = useFetchClientList({ exclude_hbs: true });
  const isClientAdmin = userType === 'CLIENTADMIN';

  const isVLAdmin = systemType === 'vl-admin';
  const isVLApp = systemType === 'vl-app';

  const currentBreakPoint = useWidthBreakpoint();
  const breakpointOrder = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];

  const currentBreakpointIndex = breakpointOrder.indexOf(currentBreakPoint);

  const isBelowBreakpoint = (breakpoint: string) => {
    const requiredIndex = breakpointOrder.indexOf(breakpoint);

    return currentBreakpointIndex >= 0 && currentBreakpointIndex < requiredIndex;
  };

  const hideSMColumns = isBelowBreakpoint('sm');
  const hideMDColumns = isBelowBreakpoint('md');
  const hideLGColumns = isBelowBreakpoint('lg');
  const hideXLColumns = isBelowBreakpoint('xl');
  const hideXXLColumns = isBelowBreakpoint('xxl');

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const zoomTargetRef = useRef<HTMLDivElement>(null);

  const { generatePatientActions } = usePatientManagementActions({ refetchFn: () => refetch(pageNumber) });

  const onClick = (_key: any, patientRow: any) => {
    const { user_guid } = patientRow;

    function encode(s: any) {
      return Buffer.from(`${s}`).toString('base64');
    }

    router.push(`/clinician/timeline360?user_guid=${encode(user_guid)}&patient_guid=${encode(user_guid)}`).then();
  };

  const viewAppointmentTimelineOption = useMemo(
    () => ({
      key: 0,
      label: 'View Appointment Timeline',
      onClick: (data: any) => {
        const { user_guid } = data;

        const m: Record<UserType, string> = {
          CLINICIAN: '/clinician/timeline360',
          PATIENT: '/patients/timeline',
          REFERRER: ConfigLink.ReferrerViewTimeLine,
          CLIENTADMIN: ConfigLink.ViewTimeLine,
          ADMIN: ConfigLink.ReferrerAdminViewTimeLine
        };
        const pathname = m[userType];

        const isPatient = userType === 'PATIENT';
        const isClinician = userType === 'CLINICIAN';

        if (!pathname) {
          console.log(`User of type "${userType}" cannot access the patient timeline`);
          return;
        }
        if (isPatient || isClinician) {
          const encode = (s) => Buffer.from(`${s}`).toString('base64');
          const queryParams = `?user_guid=${encode(user_guid)}&patient_guid=${encode(user_guid)}${isClinician ? '&team=true' : ''}`;

          router.push(pathname + queryParams);
          return;
        }

        const search = qs.stringify({
          patient_guid: data.user_guid,
          ...timelineBackQueryParam('patient')
        });

        router.push({ pathname, search });
      }
    }),
    [userType, router]
  );

  const actionsApp = [viewAppointmentTimelineOption];

  const patientColumns = useMemo(
    () => [
      ...(isClientAdmin
        ? [
            {
              display: 'VL ID',
              dataSourceKey: 'lucy_id',
              mandatory: true,
              pinned: 'left' as PinnedPosition,
              filterKey: 'lucy_id',
              filterable: {
                queryKey: 'lucy_id',
                vlId: true,
                exact: false
              }
            }
          ]
        : []),
      {
        display: 'Client',
        dataSourceKey: 'client_name',
        responsive: ['xl'],
        hidden: hideXLColumns,
        filterMultiple: false,
        filterKey: 'client_id',
        filters: clientList?.map((item) => ({ value: item.client_id, text: item.company_name }))
      },
      {
        display: 'Patient Reference',
        dataSourceKey: 'membership_no',
        responsive: ['sm'],
        hidden: hideSMColumns,
        filterable: {
          exact: false,
          queryKey: 'mem_no'
        },
        minWidth: 70
      },
      {
        display: 'Title',
        dataSourceKey: 'title',
        responsive: ['xxl'],
        hidden: hideXXLColumns,
        width: '50px',
        minWidth: 60
      },
      {
        display: 'Name',
        dataSourceKey: 'full_name',
        filterable: {
          exact: false,
          queryKey: 'name'
        }
      },
      {
        display: 'Preferred Name',
        dataSourceKey: 'pref_name'
      },
      {
        display: 'Email Address',
        dataSourceKey: 'email',
        responsive: ['lg'],
        hidden: hideLGColumns,
        resizeSize: 'large',
        filterable: {
          exact: false,
          queryKey: 'email'
        },
        onHeaderCell: () => ({ className: 'vl-table-fixed-width-header-cell' })
      },
      {
        display: 'Date of Birth',
        dataSourceKey: 'dob',
        filterable: true,
        dateFilter: true,
        dob: true,
        responsive: ['xxl'],
        hidden: hideXXLColumns,
        columnDataFormat: ({ data }: any) => (data ? moment.utc(data).format('LL') : '')
      },
      {
        display: 'Sex',
        dataSourceKey: 'sex',
        responsive: ['xxl'],
        hidden: hideXXLColumns
      },
      {
        display: 'Primary Contact Number',
        dataSourceKey: 'tel_mobile_no',
        responsive: ['xxl'],
        hidden: hideXXLColumns,
        filterable: {
          exact: false,
          queryKey: 'tel'
        }
      },
      {
        display: 'Postcode',
        dataSourceKey: 'postcode',
        responsive: ['xxl'],
        hidden: hideXXLColumns,
        filterable: {
          exact: true,
          queryKey: 'postcode'
        }
      },
      {
        display: 'Status',
        dataSourceKey: 'status',
        responsive: ['md'],
        hidden: hideMDColumns
      },
      {
        display: 'Actions',
        dataSourceKey: 'actions',
        mandatory: true,
        pinned: 'right' as PinnedPosition,
        isAction: true,
        onClick: onClick,
        ...(isVLApp
          ? {
              actionItems: actionsApp
            }
          : {
              getActionItemsFromData: (data: PatientType) => [
                viewAppointmentTimelineOption,
                ...generatePatientActions(data)
              ]
            }),
        width: '50px'
      }
    ],
    [
      clientList.length,
      isVLApp,
      hideLGColumns,
      hideMDColumns,
      hideSMColumns,
      hideXLColumns,
      hideXXLColumns,
      isClientAdmin
    ]
  );

  const closeAddPatientModal = () => setShowAddPatientModal(false);

  const onPatientCreated = () => {
    closeAddPatientModal();
    successModal({
      title: 'Success',
      content: 'Patient has been created successfuly'
    });
    refetch();
  };

  const {
    columns: customizedColumns,
    preferences,
    hasCustomPreferences,
    hasColumnLayoutCustomization,
    save: saveColumnPreferences,
    saveWidths: saveColumnWidths,
    reset: resetColumnPreferences
  } = useTableColumnSettings({
    tableId: PATIENTS_TABLE_ID,
    columns: patientColumns,
    enabled: isClientAdmin
  });

  const transformedColumns = useTransformColumns(customizedColumns);

  const {
    columns: tableColumns,
    query,
    hasActiveFilters,
    handleTableChange,
    tableKey,
    pageNumber,
    setPageNumber,
    clearFilters
  } = useColumnFilters(transformedColumns, 0);

  const fetchPatientThunk = useCallback(async () => {
    if (isVLAdmin && !hasActiveFilters) return [];

    let params = { ...query };

    if (query.mem_no) {
      // allow filtering by nhs number with spaces (ex. 485 777 3456)
      // so 485 777 3456 and 4857773456 will return the same result
      params = { ...params, mem_no: query.mem_no.replace(/\s/g, '') };
    }

    const response = await dispatch(actions.getPatientList(user.user_guid, params));
    return response;
  }, [query, hasActiveFilters]);

  const {
    page: rows,
    lastPage,
    loading,
    setLoading,
    refetch,
    error
  } = usePagedQuery(
    fetchPatientThunk,
    ['get-patient-list', pageNumber, query],
    pageNumber,
    setPageNumber,
    PAGE_SIZE,
    false
  ); // Must pass isTabbedPage=false for non-tabbed pages

  const { consumed } = useApi();

  useEffect(() => {
    if (error) {
      notification.error({
        message: t('Failed to load patients'),
        description: t('Please check your connection and refresh to try again'),
        duration: 6
      });
      consumed(error);
    }
  }, [error, consumed, t]);

  const resultTitle = () => {
    if (!rows || rows.length < 1) return 'No results found';

    if (rows.length === 1) return '1 result found';
    else if (rows.length > 1) return rows.length + ' results found';
  };

  const [displayColumnList, hideColumnList] = useReactiveColumns(tableColumns, currentBreakPoint);

  const tableZoom = useTableAutoZoom(tableContainerRef, zoomTargetRef, displayColumnList, hasColumnLayoutCustomization);

  const renderVLAppColumn = (column: any, rowData: any) =>
    column.isAction ? (
      <Dropdown
        trigger={['click']}
        placement="topRight"
        menu={{
          items: actionsApp.map(({ key, label, onClick }) => ({
            key,
            label,
            onClick: () => onClick(rowData)
          }))
        }}
      >
        <Button data-testid="actions-menu" type="text" icon={<EllipsisOutlined />} />
      </Dropdown>
    ) : (
      <DataCell item={column} rowData={rowData} />
    );

  const AdminTitleSection = adminProps?.TitleSection;

  const TitleSection = isVLApp ? (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
      <Button type="primary" onClick={clearFilters} data-testid="clear-filters-button">
        {t('Clear Filters')}
      </Button>
    </div>
  ) : (
    !!AdminTitleSection && (
      <AdminTitleSection
        onClearFilters={clearFilters}
        onCreateNewPatient={() => setShowAddPatientModal(true)}
        columnCustomization={{
          columns: patientColumns,
          preferences,
          hasCustomPreferences,
          onSave: saveColumnPreferences,
          onReset: resetColumnPreferences
        }}
      />
    )
  );

  return (
    <div>
      {TitleSection}
      <div>
        <div className="mt-5">
          <span className="vl-fs-large font-weight-bold d-block" style={{ paddingLeft: '0.3rem' }}>
            <span>{resultTitle()}</span>
          </span>
        </div>
        <Card className="shadow-sm" styles={{ body: { padding: '10px 0 0 0' } }}>
          <div ref={tableContainerRef} className="vl-common-timeline-bootstrap-overrides ">
            <div
              ref={zoomTargetRef}
              className="vl-common-bootstrap"
              data-testid="patient-search-table"
              style={{ zoom: tableZoom }}
            >
              <TableDataView
                key={`${tableKey}-${hasCustomPreferences ? 'custom' : 'default'}`}
                noDataFoundText="No patients found"
                shouldRequireSearchFilters={isVLAdmin && !hasActiveFilters}
                headerItems={displayColumnList}
                hideColumnList={hideColumnList}
                dataSource={rows}
                handleTableChange={handleTableChange}
                keyValue="user_guid"
                size="small"
                columnTransformer={isVLApp ? renderVLAppColumn : undefined}
                onColumnResize={saveColumnWidths}
              />
            </div>
          </div>
        </Card>
        <Pagination
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          isLastPage={lastPage}
          loading={loading}
          setLoading={setLoading}
        />
        <AddPatient close={closeAddPatientModal} show={showAddPatientModal} onCreated={onPatientCreated} />
      </div>
    </div>
  );
}

const PatientSearchTableWithManagementContext = (props: any) => {
  return (
    <ManagePatientContextProvider>
      <PatientSearchTable {...props} />
    </ManagePatientContextProvider>
  );
};

export default PatientSearchTableWithManagementContext;
